import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Alert, Typography } from 'antd';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const { loginWithGoogle, isLoading, error, errorSource, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Google Identity one-tap / button callback
  const handleGoogleCredential = useCallback(async (credential: string) => {
    try {
      // Decode JWT to extract email and sub (client-side decode, no verify)
      const payload = JSON.parse(atob(credential.split('.')[1]));
      const googleEmail: string = payload.email;
      const googleSub: string = payload.sub;
      await loginWithGoogle(googleEmail, googleSub, payload.name);
    } catch (e) {
      // Fallback: show error via context
      console.error('Google credential handling error:', e);
    }
  }, [loginWithGoogle]);

  // Load Google Identity script and render button
  useEffect(() => {
    const scriptId = 'google-identity';
    if (document.getElementById(scriptId)) {
      // Already loaded
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const google: any = (window as any).google;
      if (google?.accounts?.id) {
        google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '644416829207-i54nhphqi67fvc3pos2umntkq8o03dfk.apps.googleusercontent.com',
          callback: (res: { credential: string }) => handleGoogleCredential(res.credential)
        });
        const buttonDiv = document.getElementById('googleSignInDiv');
        if (buttonDiv) {
          buttonDiv.innerHTML = '';
          google.accounts.id.renderButton(buttonDiv, {
            theme: 'outline',
            size: 'large',
            text: 'continue_with',
            width: 300
          });
        }
      }
      return;
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const google: any = (window as any).google;
      if (google?.accounts?.id) {
        google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '644416829207-i54nhphqi67fvc3pos2umntkq8o03dfk.apps.googleusercontent.com',
          callback: (res: { credential: string }) => handleGoogleCredential(res.credential)
        });
        const buttonDiv = document.getElementById('googleSignInDiv');
        if (buttonDiv) {
          google.accounts.id.renderButton(buttonDiv, {
            theme: 'outline',
            size: 'large',
            text: 'continue_with',
            width: 300
          });
        }
      }
    };
    document.body.appendChild(script);
    return () => {
      // cleanup not strictly necessary for this SDK
    };
  }, [handleGoogleCredential]);

  if (isLoading && !error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #7a1f2a 0%, #5a1720 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Text style={{ fontSize: 16, color: 'white' }}>Loading...</Text>
      </div>
    );
  }

  if (isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #7a1f2a 0%, #5a1720 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          border: 'none',
        }}
        styles={{
          body: { padding: '40px 32px' },
        }}
      >
        {/* Logo and Title */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img
            src="/logo.png"
            alt="Pita Way Logo"
            style={{
              width: 80,
              height: 80,
              marginBottom: 16,
              borderRadius: 12,
            }}
          />
          <Title level={3} style={{ margin: 0, marginBottom: 8, color: '#1a1a1a' }}>
            GFS Tracking System
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Monitor and analyze GFS expenses across locations
          </Text>
        </div>

        {error && (
          <Alert
            message={errorSource === 'google' ? 'Access Denied' : 'Login Failed'}
            description={error}
            type="error"
            showIcon
            closable
            onClose={clearError}
            style={{ marginBottom: 24, borderRadius: 8 }}
          />
        )}

        {/* Google Sign In */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
        }}>
          <div style={{
            padding: '16px 24px',
            background: '#f8f9fa',
            borderRadius: 12,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}>
            <div id="googleSignInDiv" />
          </div>

          <div style={{
            textAlign: 'center',
            padding: '16px',
            background: '#fff8f0',
            borderRadius: 8,
            border: '1px solid #ffe4cc',
          }}>
            <Text style={{ fontSize: 13, color: '#8c6239' }}>
              Sign in with your <strong>@pitaway.com</strong> Google account
            </Text>
          </div>
        </div>
      </Card>

      {/* Footer */}
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
          Pita Way Restaurant Group
        </Text>
      </div>
    </div>
  );
};

export default Login;
